import { FC, useContext, useEffect, useMemo, useState } from 'react';
import TaskFiles from "../TaskFiles/TaskFiles";
import { useParams } from "react-router-dom";
import TaskPerson from "../TaskPerson/TaskPerson";
import { IFunctionPermissions } from "../../../common/api_interfaces/role/IFunctionPermissions";
import SubTasks from "../TaskRightInfo/SubTasks/SubTasks";
import { Context } from '../../..';
import { IOptionsQueryParams } from '../../../helpers/paramsAutoFiller';
import { ISubTask } from '../../../common/api_interfaces/task/ISubTask';
import { TaskContext } from '../../../contexts/TaskContext';
import { handleError } from '../../../helpers/ErrorLib/handleError';
import { AppContext } from '../../../App';
import TaskDeadlines from './TaskDeadlines/TaskDeadlines';
import TaskAttributes from './TaskAttributes/TaskAttributes';

import "./TaskRightInfo.css"
import TaskDetails from './TaskDetails/TaskDetails';
import WidgetContainer from '../../UiLib/WidgetContainer/WidgetContainer';

interface ITaskRightInfoParams {
    setSubTasks?: (tasks: ISubTask[]) => void;
    userAccess: IFunctionPermissions[];
    optionsQueryParams: IOptionsQueryParams;
}

const TaskRightInfo: FC<ITaskRightInfoParams> = ({ 
    setSubTasks, 
    userAccess, 
    optionsQueryParams 
}) => {
    const { subTasks, allowedProjects } = useContext(TaskContext);
    const { id } = useParams();
    const { store } = useContext(Context);
    const { showToast } = useContext(AppContext);
    
    const [viewTaskInfo, setViewTaskInfo] = useState(false);

    // Мемоизация проверки прав доступа
    const hasViewPermission = useMemo(() => {
        try {
            const taskActionAccess = userAccess.find((access) => 
                access.functionCode === "TaskAction"
            );
            
            return taskActionAccess?.permissions.some(
                (permission) => 
                    permission.permissionCode === "view" && 
                    permission.isGranted
            ) ?? false;
        } catch (error) {
            handleError(error, {
                defaultMessage: "Неизвестная ошибка при проверки прав доступа",
                showToast: showToast
            });
            return false;
        }
    }, [userAccess]);

    // Эффект для установки состояния прав доступа
    useEffect(() => {
        setViewTaskInfo(hasViewPermission);
    }, [hasViewPermission]);

    // Мемоизация параметров для SubTasks
    const subTasksOptionsParams = useMemo((): IOptionsQueryParams => ({
        page: optionsQueryParams.page,
        entity: "sub-task-form",
        action: optionsQueryParams.action,
        entityId: optionsQueryParams.entityId
        
    }), [optionsQueryParams.page, optionsQueryParams.action, optionsQueryParams.entityId]);

    // Проверка условий для отображения SubTasks
    const shouldShowSubTasks = useMemo(() => 
        !store.getBlockSubtasks() && viewTaskInfo,
        [store, viewTaskInfo]
    );

    const taskId = useMemo(() => Number(id), [id]);

    return (
        <WidgetContainer>
            <div className="task-right-info-component">
                <div className='task-right-info-block'>
                    <TaskDeadlines/>
                </div>
                <div className='task-right-info-line'></div>
                <div className='task-right-info-block'>
                    <TaskAttributes/>
                </div>
                <div className='task-right-info-line'></div>
                <div className='task-right-info-block'>
                    <TaskDetails/>
                </div>
                <div className='task-right-info-line'></div>
                <div className='task-right-info-block'>
                    <TaskFiles userAccess={userAccess} />
                </div>
                <div className='task-right-info-line'></div>
                <div className='task-right-info-block'>
                    {shouldShowSubTasks && (
                        <SubTasks 
                            subtaskList={subTasks} 
                            allowedProjects={allowedProjects} 
                            setSubTasks={setSubTasks} 
                            taskId={taskId} 
                            userAccess={userAccess}
                            optionsQueryParams={subTasksOptionsParams}
                        />
                    )}
                </div>
                <div className='task-right-info-line'></div>
                <div className='task-right-info-block'>
                    <TaskPerson permission={viewTaskInfo} />
                </div>
            </div>
        </WidgetContainer>
    );
};

export default TaskRightInfo;